/**
 * Reasons the platform itself writes on a record (a domain lock it places, the audit entry of a
 * read it makes, a work item it closes) are technical English: "post-registration lock (transfer
 * policy)", "carry unchanged identity data into a new subject version", "COURT_ORDER dispute
 * 12/2026/QD (People's Court …)". They read in the operator's language here, the original one hover
 * away; a reason a person typed is shown as it was typed.
 *
 * The set is every reason the Rust sources write (tests/system-reason.test.mjs reads them): an
 * exact text, or a `format!` template whose values are read in turn (by the vocabulary named for
 * the value's place, a value the platform names, `systemReason.value.*`, or a reason of its own).
 * A journal entry's description reads by `journalDescriptionWith` (a refund's reversal carries the
 * refund's reason; a wallet adjustment's is the staff member's text).
 * A registry's own answer ("registry 2306: …", a workflow's "REGISTRY_REJECTED 2201: …") reads as
 * its EPP result, the registry's text kept as the technical detail; a workflow's failure code (the
 * reason of an order item's refund: "COMPLIANCE_BLOCKED") by the failure vocabulary
 * (`domains:vocab.failure`).
 *
 * A plain function with a lookup (`systemReason.*` in the `common` namespace, `ns:key` for another
 * namespace's vocabulary) so tests run in Node.
 */

import { DISPUTE_TYPES } from '../pages/compliance/vocab.ts';
import { eppResultWith } from './eppResult.ts';

export type ReasonLookup = (key: string) => string | undefined;

/** A reason in words; `detail` is the registry's own text when the reason quotes one. */
export interface ReasonText {
  text: string;
  detail: string | null;
}

/** Exact reasons → `systemReason.<key>`. */
const FIXED: Record<string, string> = {
  'post-registration lock (transfer policy)': 'postRegistrationLock',
  'post-transfer lock (transfer policy)': 'postTransferLock',
  'change-of-registrant lock (transfer policy)': 'changeOfRegistrantLock',
  // The registrar lock (clientTransferProhibited) staff or the registrant set.
  'registrant transfer lock': 'registrarLock',
  'unlocked by registrant': 'unlockedByRegistrant',
  'time-boxed lock expired': 'lockExpired',
  'name change completed: full client lock (as VNNIC after registration)': 'nameChangeLock',
  'registry lock removed at the registry': 'registryLockRemoved',
  'confirmed by staff': 'confirmedByStaff',
  'check at the registry': 'checkAtRegistry',
  'the registry shows the change (domain:info)': 'registryShowsChange',
  'name-change request cancelled': 'nameChangeCancelled',
  'registration cancelled by staff': 'registrationCancelled',
  'registration resumed by staff': 'registrationResumed',
  'registration reactivated by staff': 'registrationReactivated',
  'the registration ended without the domain being created': 'registrationEndedUncreated',
  'domain registered': 'domainRegistered',
  'the domain was deleted': 'domainDeleted',
  'the upstream registrar no longer reports it': 'upstreamNoLonger',
  // The reporting feed's stored reset and suspension codes (crates/reporting: guard, sync, consumers, keys).
  RESTART: 'reportingRestart',
  EPOCH_CHANGED: 'reportingEpochChanged',
  PROJECTION_CHANGED: 'reportingProjectionChanged',
  WEEKLY_RESWEEP: 'reportingWeeklyResweep',
  SUSPENDED: 'reportingSuspended',
  EXPOSED_IN_URL: 'reportingExposedInUrl',
  BUDGET_EXCEEDED: 'reportingBudgetExceeded',
  NETWORK_NOT_ALLOWED: 'reportingNetworkNotAllowed',
  'submitted in the VNNIC panel': 'submittedInPanel',
  // Reads of identity data and files, audited with their purpose.
  'carry unchanged identity data into a new subject version': 'carryIdentity',
  'carry the former account identity data into the primary subject': 'carryAccountIdentity',
  'identity data carried over from the customer account (ADR-022)': 'carryCustomerIdentity',
  'eKYC OCR comparison with the declared subject version': 'ekycComparison',
  'automated eKYC verification (VNNIC OCR / FaceID)': 'ekycVerification',
  'automated document verification': 'documentVerification',
  'VNNIC HSTM dossier backup': 'hstmBackup',
  'VNNIC Registrar API business registry lookup': 'businessRegistryLookup',
  'VNNIC Registrar API promotion code request': 'promotionCodeRequest',
  'declaration (bản khai)': 'declaration',
  'declaration (bản khai): legal representative': 'declarationRepresentative',
  'declaration check: generated PDF': 'declarationCheckGenerated',
  'declaration check: uploaded file': 'declarationCheckUploaded',
  'declaration download by its registrant': 'declarationDownload',
  'dossier file: signature check': 'dossierFileSignatureCheck',
  "registrar signature of the dossier's file": 'registrarSignature',
  'registrar signature backfill: the signed declaration and the file it replaced': 'signatureBackfill',
  // The reason of a subject version a proof of identity makes (identity_subjects::proof): an eKYC,
  // or a HostBill eKYC / signature-check row imported (identity-worker import-hostbill-ekyc).
  EKYC: 'proofEkyc',
  DIGITAL_SIGNATURE: 'proofDigitalSignature',
  // A post-operation the registrar accepted by signing it (post_operations.rs).
  'NĐK duyệt khi ký': 'acceptedWhenSigning',
  // Work items the platform closes.
  'superseded by a new upload of the declaration': 'declarationSuperseded',
  'the domain declaration was signed by the registrar (manual request)': 'declarationSignedManual',
  'the dossier now passes the checks before signing': 'dossierPassesChecks',
  "the registrar's signature was recorded as the confirmation": 'signatureRecorded',
  'the underlying problem is no longer present': 'problemGone',
  'the dossier was completed or superseded, or the domain is closed': 'dossierClosed',
  'the registration was cancelled by staff': 'registrationCancelled',
  'the notification was sent again': 'notificationResent',
  'the e-invoice was issued': 'einvoiceIssued',
  // Dossier status changes.
  'content changed after a correction request': 'contentChangedAfterCorrection',
  'change of registrant': 'changeOfRegistrant',
  'registrant data corrected before the registry create (staff retry)': 'registrantCorrected',
  // Accounts and subjects created together.
  'primary subject created with the customer': 'primarySubjectCreated',
  'legal representative entered with the new customer': 'representativeWithCustomer',
  'legal representative set on the subject form': 'representativeOnForm',
  'sign-in created with the account': 'signInCreated',
  // Sign-in outcomes (staff and customer).
  'bad password or code': 'badPasswordOrCode',
  'unknown account': 'unknownAccount',
  'locked or inactive': 'lockedOrInactive',
  'mfa required': 'mfaRequired',
  mfa: 'mfa',
  'password only': 'passwordOnly',
  'OT&E quick sign-in': 'quickSignIn',
  'OT&E quick sign-in, mfa': 'quickSignInMfa',
  'wrong e-mail or password': 'wrongEmailOrPassword',
  'wrong code': 'wrongCode',
  'too many wrong passwords': 'tooManyWrongPasswords',
  'too many attempts from this address': 'tooManyFromAddress',
  // The DNS login of a domain (dns-client-api).
  'wrong domain or DNS password': 'wrongDomainOrDnsPassword',
  'too many wrong DNS passwords from this address': 'tooManyWrongDnsPasswords',
  'e-mail not verified': 'emailNotVerified',
  'not a member': 'notAMember',
  'signed out': 'signedOut',
  // The anti-robot check of the client sign-in forms (Cloudflare Turnstile).
  'anti-robot check not valid': 'captchaInvalid',
  'anti-robot check expired or already used': 'captchaExpiredOrUsed',
  'anti-robot check solved on another site': 'captchaWrongSite',
  'anti-robot check solved for another form': 'captchaWrongForm',
  // Refunds of an order item whose operation failed (commerce_orders::fail_item).
  'registry rejected renewal': 'registryRejectedRenewal',
  // Journal entry descriptions of the removed provider port's top-ups (billing::payments, ADR-028
  // stage 5): older entries keep them.
  'payment settled to wallet': 'paymentSettled',
  // A refund to the payer's source the gateway did not make: its payout reversed (payment engine refunds).
  'refund to the payer failed': 'refundToPayerFailed',
};

/**
 * `format!` templates → `systemReason.<key>`: `{{1}}`, `{{2}}`, … are the values, read in turn. A
 * value whose place names a vocabulary (`ns:path`, after the key) reads by it; any other reads as a
 * value the platform names, a reason of its own, or as it is.
 */
const TEMPLATES: [RegExp, string, ...(string | undefined)[]][] = [
  // cases::disputes: `<dispute type> dispute <case reference> (<provider>)`
  [new RegExp(`^(${DISPUTE_TYPES.join('|')}) dispute (.+?) \\((.*)\\)$`), 'dispute', 'compliance:enums.disputeType'],
  [/^name-change request closed: (.+)$/s, 'nameChangeClosed'],
  [/^name-change request (\w+)$/, 'nameChangeEnded'],
  [/^panel operation (\w+): (.+)$/s, 'panelOperation'],
  [/^add-on cancelled: (.+)$/s, 'addonCancelled'],
  [/^registry refused the lock: (.+)$/s, 'registryRefusedLock'],
  [/^password; next (\w+)$/, 'passwordNext'],
  [/^second factor (\w+)$/, 'secondFactor'],
  // step-up: the factor accepted
  [/^(otp|rcv)$/, 'stepUp'],
  // dossier: `<MANUAL|AUTOMATIC>: <rule> (verification method <method>)`
  [/^([A-Z_]+): (\w+) \(verification method (.+)\)$/, 'dossierDecision', undefined, undefined, 'accounts:enum.verificationMethod'],
  [/^staff review (\S+) \(([A-Z_]+)\)$/, 'staffReview', undefined, 'work:kind'],
  [/^([A-Z_]+) \(name-change request (\S+)\)$/, 'nameChangeDossier', 'services:enum.ncCase'],
  [/^return of the domain \(declaration (\S+)\)$/, 'domainReturn'],
  // identity: the subject version a submitted signed declaration makes (`PROOF_SUBMISSION <submission>:
  // <purpose> declaration <id> submitted (file <version>)`), a proof of identity accepted
  [/^(PROOF_SUBMISSION) (\S+): (registration|subject declaration) declaration (\S+) submitted \(file (\S+)\)$/, 'proofSubmission'],
  [/^accepted proof of identity: verification (\S+)$/, 'proofAccepted'],
  // dossier_files::pick_up: a file uploaded with no check, decision or submission, submitted now
  [
    /^RECOVERED: file (\S+) uploaded (.+?) by (\w+) had no check, decision or submission; submitted to its review path \(owner decision 2026-10-06\)$/,
    'fileRecovered',
    undefined,
    undefined,
    'accounts:enum.actorType',
  ],
  [/^registry contact (\S+)$/, 'registryContact'],
  [/^(\S+) session\(s\) revoked$/, 'sessionsRevoked'],
  // a workflow's refusal of the domain's state (restore, renewal)
  [/^domain is ([A-Z_]+)$/, 'domainState', 'domains:status.lifecycle'],
  [/^domain is ([A-Z_]+); it cannot be renewed \(restore it if it is in redemption\)$/, 'cannotRenew', 'domains:status.lifecycle'],
  // journal entry descriptions: a registry cost accrual, an auction's or a backorder's capture
  [/^registry cost (\S+)$/, 'registryCost'],
  [/^Auction (\S+)$/, 'auction'],
  [/^Backorder catch (\S+)$/, 'backorderCatch'],
  // a wallet top-up by bank transfer: `bank transfer <payment code>` (billing::bank_transfer)
  [/^bank transfer (\S+)$/, 'bankTransfer'],
  // a refund to the payer's source, out of the wallet: `refund to the payer: <staff reason>` (payment engine refunds)
  [/^refund to the payer: (.+)$/s, 'refundToPayer'],
];

/** The entry type whose description is a staff member's reason, `(ref <reference>)` appended when they entered one (billing::adjustments). */
const WALLET_ADJUSTMENT = 'WALLET_ADJUSTMENT';
const WITH_REFERENCE = /^(.+) \(ref (.+)\)$/s;
/** An order item's hold capture: `<order operation> <domain name>` (commerce_orders), the operation by `commerce:enum.operation`. */
const CAPTURE = /^([A-Z_]+) (\S+)$/;

/** `registry <EPP result code>: <message>` (a refused promotion code): the result, the registry's text as the detail. */
const EPP = /^registry (\d{4}): (.+)$/s;
/** A workflow's registry refusal: `REGISTRY_REJECTED <code>[: <message>]`, `REGISTRY_REJECTED:<code>` (0: no result code). */
const REJECTED = /^REGISTRY_REJECTED[ :](\d+)(?:: (.+))?$/s;
/** A workflow's failure code (`with(…, "failure", …)` in crates/lifecycle). */
const FAILURE = /^[A-Z][A-Z0-9_]*$/;

const fill = (template: string, values: string[]) => template.replace(/\{\{(\d+)\}\}/g, (_, n: string) => values[Number(n) - 1] ?? '');

/** The reason in the operator's language, or null when it is not one the platform writes. */
export function systemReasonWith(reason: string, lookup: ReasonLookup): ReasonText | null {
  const text = reason.trim();
  const fixed = FIXED[text];
  if (fixed) {
    const t = lookup(`systemReason.${fixed}`);
    return t ? { text: t, detail: null } : null;
  }
  const epp = EPP.exec(text);
  if (epp) return eppResultWith(Number(epp[1]), epp[2] ?? null, lookup);
  const rejected = REJECTED.exec(text);
  if (rejected && /^\d{4}$/.test(rejected[1] ?? '')) return eppResultWith(Number(rejected[1]), rejected[2] ?? null, lookup);
  if (rejected || FAILURE.test(text)) {
    const t = lookup(`domains:vocab.failure.${rejected ? 'REGISTRY_REJECTED' : text}`);
    return t ? { text: t, detail: rejected?.[2] ?? null } : null;
  }
  for (const [re, key, ...vocab] of TEMPLATES) {
    const m = re.exec(text);
    if (!m) continue;
    const template = lookup(`systemReason.${key}`);
    if (!template) return null;
    let detail: string | null = null;
    const value = (v: string, i: number): string => {
      // a word, or words (a declaration purpose as placed: "subject declaration")
      const code = /^[A-Za-z_]+(?: [A-Za-z_]+)*$/.test(v);
      const named = code ? lookup(vocab[i] ? `${vocab[i]}.${v}` : `systemReason.value.${v}`) : undefined;
      if (named !== undefined) return named;
      const nested = vocab[i] ? null : systemReasonWith(v, lookup);
      detail ??= nested?.detail ?? null;
      return nested?.text ?? v;
    };
    return { text: fill(template, m.slice(1).map((v, i) => value(v ?? '', i))), detail };
  }
  return null;
}

/**
 * A journal entry's description in the operator's language, by the entry's type when the page has
 * it. A wallet adjustment's is the staff member's reason as typed: only the reference they entered
 * is named. Any other entry's is the platform's words (a refund's reversal carries the refund's
 * reason; a hold capture names its order operation and domain). Null when nothing is to be named:
 * shown as it is.
 */
export function journalDescriptionWith(description: string, entryType: string | null | undefined, lookup: ReasonLookup): ReasonText | null {
  const text = description.trim();
  if (entryType === WALLET_ADJUSTMENT) {
    const m = WITH_REFERENCE.exec(text);
    const template = m ? lookup('systemReason.withReference') : undefined;
    return m && template ? { text: fill(template, [m[1] ?? '', m[2] ?? '']), detail: null } : null;
  }
  const own = systemReasonWith(text, lookup);
  if (own) return own;
  const capture = CAPTURE.exec(text);
  const operation = capture ? lookup(`commerce:enum.operation.${capture[1]}`) : undefined;
  const template = operation ? lookup('systemReason.capture') : undefined;
  return capture && operation && template ? { text: fill(template, [operation, capture[2] ?? '']), detail: null } : null;
}
